import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  Link
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Tune as TuneIcon,
  Shield as ShieldIcon,
  Smartphone as SmartphoneIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Gavel as GavelIcon,
  ContactSupport as ContactSupportIcon,
  Update as UpdateIcon,
  Help as HelpIcon,
  VerifiedUser as VerifiedUserIcon,
  Share as ShareIcon
} from '@mui/icons-material';

function CookiePolicy() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Smooth scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sections = [
    {
      id: 'intro',
      title: 'Introduction',
      icon: <InfoIcon />,
      color: '#1976d2'
    },
    {
      id: 'what-are-cookies',
      title: 'What Are Cookies?',
      icon: <CookieIcon />,
      color: '#388e3c'
    },
    {
      id: 'why-we-use',
      title: 'Why We Use Cookies',
      icon: <VerifiedUserIcon />,
      color: '#f57c00'
    },
    {
      id: 'categories',
      title: 'Cookie Categories',
      icon: <TuneIcon />,
      color: '#7b1fa2'
    },
    {
      id: 'hosting',
      title: 'Hosting & Infrastructure',
      icon: <StorageIcon />,
      color: '#d32f2f'
    },
    {
      id: 'payment',
      title: 'Payment Processing',
      icon: <SecurityIcon />,
      color: '#0288d1'
    },
    {
      id: 'mobile',
      title: 'Mobile App Data',
      icon: <SmartphoneIcon />,
      color: '#e64a19'
    },
    {
      id: 'management',
      title: 'Cookie Management',
      icon: <SettingsIcon />,
      color: '#00796b'
    },
    {
      id: 'third-party',
      title: 'Third-Party Services',
      icon: <ShareIcon />,
      color: '#5e35b1'
    },
    {
      id: 'data-transfers',
      title: 'International Transfers',
      icon: <AnalyticsIcon />,
      color: '#c2185b'
    },
    {
      id: 'retention',
      title: 'Data Retention',
      icon: <StorageIcon />,
      color: '#795548'
    },
    {
      id: 'rights',
      title: 'Your Privacy Rights',
      icon: <GavelIcon />,
      color: '#607d8b'
    },
    {
      id: 'security',
      title: 'Security Measures',
      icon: <ShieldIcon />,
      color: '#ff5722'
    },
    {
      id: 'updates',
      title: 'Policy Updates',
      icon: <UpdateIcon />,
      color: '#9c27b0'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4, pt: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold', 
            color: '#1976d2',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}
        >
          Circledin Cookie Policy
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Effective Date: January 1, 2025 | Last Updated: January 1, 2025 | Version: 1.0
        </Typography>
        <Chip 
          label="Current Version" 
          color="primary" 
          sx={{ 
            mt: 1,
            fontSize: '0.875rem',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
          }} 
        />
      </Box>

      {/* Quick Overview */}
      <Paper 
        elevation={6} 
        sx={{ 
          p: 5, 
          mb: 6, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Cookie Transparency
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mb: 4, opacity: 0.9 }}>
          We believe in clear, transparent communication about how we use cookies and tracking technologies.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  User Control
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Full control over your cookie preferences and settings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Clear Purpose
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Transparent explanation of why each cookie is used
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Easy Opt-Out
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Simple tools to manage and disable cookies
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Important Notice */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 5, 
          p: 3,
          borderRadius: '12px',
          border: '1px solid #2196f3',
          boxShadow: '0 4px 16px rgba(33, 150, 243, 0.1)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          Important: This Cookie Policy should be read together with our Privacy Policy and Terms of Service.
        </Typography>
        <Typography variant="body1">
          By continuing to use our Service, you consent to our use of cookies as described in this policy.
        </Typography>
      </Alert>

      {/* Table of Contents */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mb: 5,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            fontWeight: 'bold',
            color: '#1976d2',
            mb: 3
          }}
        >
          <HelpIcon sx={{ fontSize: '2rem' }} /> Table of Contents
        </Typography>
        <Grid container spacing={3}>
          {sections.map((section, index) => (
            <Grid item xs={12} sm={6} md={4} key={section.id}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={section.icon}
                onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: section.color, 
                  borderColor: section.color,
                  borderRadius: '12px',
                  p: 2,
                  height: 'auto',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${section.color}10`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${section.color}30`
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {index + 1}. {section.title}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Cookie Policy Content */}
      <Box sx={{ mb: 6 }}>
        
        {/* Section 1: Introduction */}
        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleChange('panel1')} 
          id="intro"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <InfoIcon sx={{ mr: 2, color: '#1976d2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>1. Introduction</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography paragraph>
              This Cookie Policy explains how Circledin LLC ("Circledin," "we," "us," or "our") uses cookies and similar tracking technologies when you visit our website, use our mobile application, or interact with our services (collectively, the "Service").
            </Typography>
            <Typography paragraph>
              This Cookie Policy should be read together with our Privacy Policy and Terms of Service. By continuing to use our Service, you consent to our use of cookies as described in this policy.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Section 2: What Are Cookies */}
        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleChange('panel2')} 
          id="what-are-cookies"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <CookieIcon sx={{ mr: 2, color: '#388e3c' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>2. What Are Cookies?</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Definition</Typography>
            <Typography paragraph>
              Cookies are small text files that are stored on your device (computer, tablet, smartphone) when you visit a website. They help websites remember information about your visit, such as your preferences and login status.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Types of Cookies by Lifespan</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Session Cookies" 
                  secondary="Temporary cookies that are deleted when you close your browser"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Persistent Cookies" 
                  secondary="Cookies that remain on your device for a set period or until you delete them"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Secure Cookies" 
                  secondary="Cookies that are only sent over encrypted HTTPS connections"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="HttpOnly Cookies" 
                  secondary="Cookies that cannot be accessed through client-side scripts"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Types of Cookies by Ownership</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="First-Party Cookies" 
                  secondary="Set directly by Circledin"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Third-Party Cookies" 
                  secondary="Set by external services we use"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 3: Why We Use Cookies */}
        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleChange('panel3')} 
          id="why-we-use"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <VerifiedUserIcon sx={{ mr: 2, color: '#f57c00' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>3. Why We Use Cookies</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography paragraph>We use cookies to:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Keep you logged in to your account" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Remember your preferences and settings" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide personalized content and recommendations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Analyze how our Service is used to improve functionality" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Prevent fraud and enhance security" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Process payments securely" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide customer support" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Show relevant advertisements" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Comply with legal and regulatory requirements" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 4: Cookie Categories */}
        <Accordion 
          expanded={expanded === 'panel4'} 
          onChange={handleChange('panel4')} 
          id="categories"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <TuneIcon sx={{ mr: 2, color: '#7b1fa2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>4. Categories of Cookies We Use</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Essential Cookies</Typography>
            <Typography paragraph>
              These cookies are necessary for our Service to function properly and cannot be disabled.
            </Typography>
            <Typography paragraph>
              <strong>Purpose:</strong> Authentication, security, session management, load balancing<br/>
              <strong>Duration:</strong> Session to 1 year<br/>
              <strong>Can be disabled:</strong> No
            </Typography>
            <Typography paragraph><strong>Examples of Essential Cookies:</strong></Typography>
            <List>
              <ListItem>
                <ListItemText primary="Session management cookies for maintaining your login state" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Security tokens for preventing unauthorized access" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Load balancing cookies for optimal performance" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User preference cookies for basic functionality" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Performance and Analytics Cookies</Typography>
            <Typography paragraph>
              These cookies help us understand how visitors interact with our Service.
            </Typography>
            <Typography paragraph>
              <strong>Purpose:</strong> Website analytics, performance monitoring, error reporting<br/>
              <strong>Duration:</strong> 13 months to 2 years<br/>
              <strong>Can be disabled:</strong> Yes
            </Typography>
            <Typography paragraph>
              We use industry-standard web analytics services, including but not limited to Google Analytics, to collect information such as page views, traffic sources, device information, and website performance metrics.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Functional Cookies</Typography>
            <Typography paragraph>
              These cookies enable enhanced functionality and personalization.
            </Typography>
            <Typography paragraph>
              <strong>Purpose:</strong> User preferences, language settings, form data, customer support features<br/>
              <strong>Duration:</strong> 30 days to 1 year<br/>
              <strong>Can be disabled:</strong> Yes (may affect functionality)
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Customer Support Platforms: Enable live chat, help desk functionality, and support ticket management" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User Interface Preferences: Remember your display settings, language choices, and interface customizations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Form Management: Save partially completed forms and user input data" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Security and Fraud Prevention Cookies</Typography>
            <Typography paragraph>
              These cookies help protect against fraudulent activity and enhance security.
            </Typography>
            <Typography paragraph>
              <strong>Purpose:</strong> Fraud detection, risk assessment, account security<br/>
              <strong>Duration:</strong> Session to 1 year<br/>
              <strong>Can be disabled:</strong> No (essential for security)
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Payment Security Systems: Fraud detection and prevention for financial transactions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Authentication Services: Secure login and identity verification systems" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Risk Assessment Tools: Behavioral analysis for suspicious activity detection" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Marketing and Advertising Cookies</Typography>
            <Typography paragraph>
              These cookies are used to deliver personalized advertisements and track marketing campaigns.
            </Typography>
            <Typography paragraph>
              <strong>Purpose:</strong> Ad personalization, campaign tracking, retargeting, conversion measurement<br/>
              <strong>Duration:</strong> 30 days to 2 years<br/>
              <strong>Can be disabled:</strong> Yes
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Digital Advertising Platforms: Track ad performance and user engagement" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Conversion Tracking: Measure the effectiveness of marketing campaigns" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Retargeting Services: Show relevant ads to previous website visitors" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Social Media Cookies</Typography>
            <Typography paragraph>
              These cookies are set by social media platforms when you interact with their content.
            </Typography>
            <Typography paragraph>
              <strong>Purpose:</strong> Social media integration, sharing functionality, social login<br/>
              <strong>Duration:</strong> Varies by platform<br/>
              <strong>Can be disabled:</strong> Yes (may affect social features)
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Social login and authentication services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Content sharing and social media buttons" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Social media plugins and embedded content" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 5: Hosting and Infrastructure */}
        <Accordion 
          expanded={expanded === 'panel5'} 
          onChange={handleChange('panel5')} 
          id="hosting"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <StorageIcon sx={{ mr: 2, color: '#d32f2f' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>5. Hosting and Infrastructure Cookies</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Cloud Hosting Services</Typography>
            <Typography paragraph>Our hosting infrastructure may set cookies for:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Load balancing and traffic distribution" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Content delivery optimization" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Security monitoring and DDoS protection" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Performance analytics and optimization" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Geographic routing and latency reduction" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Content Delivery Networks</Typography>
            <Typography paragraph>We use content delivery network services that may set cookies for:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Caching optimization" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Geographic content distribution" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Performance monitoring" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Security and access control" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 6: Payment Processing */}
        <Accordion 
          expanded={expanded === 'panel6'} 
          onChange={handleChange('panel6')} 
          id="payment"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <SecurityIcon sx={{ mr: 2, color: '#0288d1' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>6. Payment Processing Cookies</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Payment Service Providers</Typography>
            <Typography paragraph>We use third-party payment processing services that set cookies for:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Secure payment form functionality" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Fraud detection and prevention" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Payment method validation" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Transaction security and encryption" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Compliance with financial regulations (PCI DSS)" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Financial Security</Typography>
            <Typography paragraph>Payment-related cookies help ensure:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Secure transmission of payment data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Prevention of payment fraud" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Compliance with banking regulations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Protection of financial information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Transaction integrity and validation" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 7: Mobile App Data */}
        <Accordion 
          expanded={expanded === 'panel7'} 
          onChange={handleChange('panel7')} 
          id="mobile"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <SmartphoneIcon sx={{ mr: 2, color: '#e64a19' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>7. Mobile App Data Collection</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Mobile Identifiers</Typography>
            <Typography paragraph>Our mobile app may collect:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Device identifiers for app functionality" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Advertising identifiers (where permitted)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Push notification tokens" />
              </ListItem>
              <ListItem>
                <ListItemText primary="App usage analytics and performance data" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Mobile Analytics</Typography>
            <Typography paragraph>We use mobile analytics services that may store:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="App performance and crash data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User interaction patterns" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Feature usage statistics" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Device and platform information" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 8: Cookie Management */}
        <Accordion 
          expanded={expanded === 'panel8'} 
          onChange={handleChange('panel8')} 
          id="management"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <SettingsIcon sx={{ mr: 2, color: '#00796b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>8. Cookie Consent and Management</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Consent Requirements</Typography>
            <Typography paragraph>
              Under various privacy laws (GDPR, CCPA, etc.), we may need your consent for certain cookies. We provide:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Clear information about cookie types and purposes" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Granular consent options for different cookie categories" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Easy opt-out mechanisms and preference management" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regular consent renewal and confirmation" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Managing Your Cookie Preferences</Typography>
            <Typography paragraph><strong>Browser Settings:</strong></Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Chrome" 
                  secondary="Settings > Privacy and Security > Cookies and other site data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Firefox" 
                  secondary="Settings > Privacy & Security > Cookies and Site Data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Safari" 
                  secondary="Preferences > Privacy > Manage Website Data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Edge" 
                  secondary="Settings > Cookies and site permissions"
                />
              </ListItem>
            </List>

            <Typography paragraph sx={{ mt: 3 }}><strong>Opt-Out Tools:</strong></Typography>
            <List>
              <ListItem>
                <ListItemText primary="Google Analytics: Google Analytics Opt-out Browser Add-on" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Network Advertising Initiative: NAI Consumer Opt-out" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Digital Advertising Alliance: DAA WebChoices Tool" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Your Online Choices (EU): youronlinechoices.eu" />
              </ListItem>
            </List>

            <Alert severity="warning" sx={{ mt: 3, borderRadius: '8px' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Impact of Disabling Cookies:
              </Typography>
              <Typography variant="body2">
                If you disable cookies, you may experience inability to stay logged in, loss of personalized settings, reduced functionality, and limited access to certain features.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Section 9: Third-Party Services */}
        <Accordion 
          expanded={expanded === 'panel9'} 
          onChange={handleChange('panel9')} 
          id="third-party"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <ShareIcon sx={{ mr: 2, color: '#5e35b1' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>9. Third-Party Services and Data Sharing</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Service Provider Categories</Typography>
            <Typography paragraph>We work with various categories of third-party service providers:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Web analytics and performance monitoring services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Payment processing and financial services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Customer support and communication platforms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Cloud hosting and infrastructure providers" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Marketing and advertising platforms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Security and fraud prevention services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Social media and integration platforms" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Data Protection Standards</Typography>
            <Typography paragraph>All third-party services we use are required to:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Maintain appropriate data security measures" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Comply with applicable privacy laws and regulations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide transparency about their data practices" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Honor user privacy choices and opt-out requests" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Implement industry-standard security protocols" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 10: International Data Transfers */}
        <Accordion 
          expanded={expanded === 'panel10'} 
          onChange={handleChange('panel10')} 
          id="data-transfers"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <AnalyticsIcon sx={{ mr: 2, color: '#c2185b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>10. International Data Transfers</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Cross-Border Data Processing</Typography>
            <Typography paragraph>
              Some of our service providers may process your data internationally. We ensure adequate protection through:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Standard Contractual Clauses (SCCs) approved by regulatory authorities" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Adequacy decisions recognizing equivalent privacy protections" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Privacy frameworks and certification programs" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Additional contractual and technical safeguards" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Geographic Compliance</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="EU/EEA" 
                  secondary="Full GDPR compliance with explicit consent requirements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="California" 
                  secondary="CCPA/CPRA compliance with comprehensive privacy rights"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Other US States" 
                  secondary="Compliance with applicable state privacy laws"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="International" 
                  secondary="Adherence to local privacy regulations where applicable"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 11: Data Retention */}
        <Accordion 
          expanded={expanded === 'panel11'} 
          onChange={handleChange('panel11')} 
          id="retention"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <StorageIcon sx={{ mr: 2, color: '#795548' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>11. Data Retention and Deletion</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Cookie Retention Periods</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Session Cookies" 
                  secondary="Automatically deleted when browser session ends"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Authentication Cookies" 
                  secondary="Typically 30 days to 1 year"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics Cookies" 
                  secondary="Usually 13-26 months"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Marketing Cookies" 
                  secondary="Generally 30 days to 2 years"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Functional Cookies" 
                  secondary="Typically 30 days to 1 year"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Automated Data Deletion</Typography>
            <Typography paragraph>We implement automated processes to:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Delete expired cookies according to retention schedules" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Remove inactive user data after specified periods" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Clear temporary and session data regularly" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Purge marketing data upon opt-out requests" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Comply with legal data retention requirements" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 12: Your Privacy Rights */}
        <Accordion 
          expanded={expanded === 'panel12'} 
          onChange={handleChange('panel12')} 
          id="rights"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <GavelIcon sx={{ mr: 2, color: '#607d8b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>12. Your Privacy Rights</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Under GDPR (EU/EEA Residents)</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Right to withdraw consent for non-essential cookies" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to access information about cookies we use" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to request deletion of cookie-related data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to object to certain types of data processing" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to data portability for your information" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Under CCPA/CPRA (California Residents)</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Right to know about cookie collection and use" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to request deletion of personal information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to opt-out of sale or sharing of personal information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to non-discrimination for exercising privacy rights" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to correct inaccurate personal information" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Under Other State Privacy Laws</Typography>
            <Typography paragraph>
              We comply with privacy rights under applicable state laws including Virginia VCDPA, Colorado CPA, Connecticut CTDPA, and other emerging state privacy regulations.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Section 13: Security Measures */}
        <Accordion 
          expanded={expanded === 'panel13'} 
          onChange={handleChange('panel13')} 
          id="security"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <ShieldIcon sx={{ mr: 2, color: '#ff5722' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>13. Security Measures</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Cookie Security Implementation</Typography>
            <Typography paragraph>We implement multiple security measures to protect cookies:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Secure HTTPS transmission for all sensitive cookies" />
              </ListItem>
              <ListItem>
                <ListItemText primary="HttpOnly flags to prevent client-side script access" />
              </ListItem>
              <ListItem>
                <ListItemText primary="SameSite attributes for cross-site request forgery protection" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regular security audits and vulnerability assessments" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Encryption of sensitive cookie data" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Data Protection Protocols</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Multi-layered access controls and authentication systems" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regular security training for all personnel" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Incident response procedures and breach notification protocols" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Continuous monitoring and threat detection systems" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Industry-standard encryption and security practices" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 14: Policy Updates */}
        <Accordion 
          expanded={expanded === 'panel14'} 
          onChange={handleChange('panel14')} 
          id="updates"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <UpdateIcon sx={{ mr: 2, color: '#9c27b0' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>14. Updates and Policy Changes</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Policy Modification Process</Typography>
            <Typography paragraph>We may update this Cookie Policy to reflect:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Changes in our cookie usage or data practices" />
              </ListItem>
              <ListItem>
                <ListItemText primary="New third-party services or technology implementations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Updates to legal or regulatory requirements" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Improvements in privacy protection measures" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User feedback and industry best practices" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Notification Methods</Typography>
            <Typography paragraph>We will notify users of material changes through:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Email notifications to registered users" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Prominent website banners and notifications" />
              </ListItem>
              <ListItem>
                <ListItemText primary="In-app notifications and alerts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Social media and other communication channels" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Updated policy posting with change highlights" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Effective Date of Changes</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Material changes take effect 30 days after notification" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Minor updates take effect immediately upon posting" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Continued use indicates acceptance of updated terms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Users may opt-out if they disagree with changes" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        mt: 8, 
        textAlign: 'center', 
        borderTop: '2px solid #e3f2fd', 
        pt: 4,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        borderRadius: '12px',
        p: 4
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
          Questions about Cookies?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Contact us at <Link href="mailto:privacy@circledin.com">support@circledin.com</Link>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Last Updated: January 1, 2025 | Version: 1.0
        </Typography>
        <Typography variant="body2" color="text.secondary">
          © 2025 Circledin LLC. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
}

export default CookiePolicy;